import React, { ReactNode, RefObject, useImperativeHandle } from 'react';

// Utilities
import { ComboBoxInputProps } from './hooks/useComboBoxInput';
import { IEntity } from '../../../../../../types/api/entities/IEntity';
import { useComboBox } from './hooks/useComboBox';

// Components
import AbstractFormsComboBox, { ComboBoxProps } from './AbstractFormsComboBox';
import ListItemEmpty from './ListItemEmpty/ListItemEmpty';
import { Section } from './types/Section';
import { TopContentResult } from './types/SuggestResultTypes';

export type SuggestComboBoxProps<T extends IEntity> = ComboBoxProps &
  ComboBoxInputProps & {
    selected?: TopContentResult;
    onSelection: (item?: T) => void;
  };

type SuggestComboBoxMapperProps<T extends IEntity, TResponse> = ComboBoxProps &
  SuggestComboBoxProps<T> & {
    responseMapper: (response: TResponse) => Section<T>[];
    children: (item: T) => ReactNode;
    ref?: RefObject<HTMLInputElement | null>;
  };

export function SuggestComboBox<T extends IEntity, TResponse>({
  children,
  onSelection,
  responseMapper,
  id,
  labelTitle,
  name,
  modifierInput,
  modifierListBox,
  inputInContainer,
  labelHidden,
  hideLabelAddition,
  labelHelpText,
  placeholder,
  resetButton,
  required,
  maxLength,
  accessKey,
  validationMessage,
  ref,
  ...inputProps
}: SuggestComboBoxMapperProps<T, TResponse>) {
  const { getComboBoxProps, formInputRef } = useComboBox(responseMapper, onSelection, inputProps);
  useImperativeHandle(ref, () => formInputRef.current as HTMLInputElement);

  return (
    // eslint-disable-next-line jsx-a11y/no-access-key
    <AbstractFormsComboBox
      id={id}
      labelTitle={labelTitle}
      name={name}
      modifierInput={modifierInput}
      modifierListBox={modifierListBox}
      inputInContainer={inputInContainer}
      labelHidden={labelHidden}
      hideLabelAddition={hideLabelAddition}
      labelHelpText={labelHelpText}
      placeholder={placeholder}
      resetButton={resetButton}
      required={required}
      maxLength={maxLength}
      accessKey={accessKey}
      ariaAutoComplete="list"
      validationMessage={validationMessage}
      {...getComboBoxProps()}
    >
      {item => (item ? children(item) : <ListItemEmpty />)}
    </AbstractFormsComboBox>
  );
}
